import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api.ts';
import { useStore } from '../lib/store.ts';
import type { MonthlySummary } from '../types/api.ts';

const monthlyKey = (iso: string) => ['monthlySummary', iso] as const;

/**
 * Fetch (live monthly stats + cached LLM summary) for one first-of-month
 * iso date. Pass null to disable the query — used when the calendar
 * month has no rides so we can skip the network round-trip.
 */
export function useMonthlySummary(iso: string | null) {
  return useQuery({
    queryKey: monthlyKey(iso ?? ''),
    queryFn: () => {
      if (!iso) throw new Error('useMonthlySummary called with null iso');
      return api.monthly.get(iso);
    },
    enabled: !!iso,
    staleTime: 60_000,
  });
}

/**
 * Generate the LLM summary for one month and write the new value back
 * into the monthlySummary cache so any rendering subscribers update
 * immediately.
 */
export function useGenerateMonthlySummary() {
  const { t } = useTranslation();
  const addToast = useStore((s) => s.addToast);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (iso: string) => api.monthly.generate(iso),
    onSuccess: (data: MonthlySummary, iso) => {
      qc.setQueryData(monthlyKey(iso), data);
    },
    onError: () => {
      addToast({ message: t('month.errorGenerate'), type: 'error' });
    },
  });
}
